// A deal's history: every stage it moved through and every edit made to it,
// newest first. `GET /api/deals/{id}/history`.
//
// The stages go back further than this CRM: a deal imported from HubSpot
// carries HubSpot's stage dates, marked as imported, and a date that was a
// HubSpot bulk edit rather than the day the deal moved says so.

import { useState, type ReactNode } from "react";
import type { Currency, DealHistoryEntry } from "@crm/shared";
import styles from "./DealHistorySection.module.css";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { formatInstant } from "../../format/calendarDate.js";
import { dealStateLabel } from "../../format/labels.js";
import { formatMoney } from "../../format/money.js";
import { useDealHistory } from "../../api/queries.js";
import { usePrincipal } from "../../auth/useAuth.js";

/** Enough to see the recent story; the rest is one click away. */
const FIRST_SHOWN = 12;

/** The fields a person edits, in their words. Anything else shows its column name. */
const FIELD_LABEL: Readonly<Record<string, string>> = {
  win_prob: "Win probability",
  estimated_recurring_monthly_minor: "Recurring estimate (monthly)",
  estimated_implementation_minor: "Implementation estimate",
  estimated_go_live: "Expected go-live",
  customer_name: "Customer",
  accounts: "Accounts",
  region: "Region",
  currency: "Currency",
  partner_id: "Partner",
  agreement_id: "Agreement",
  invoice_recipient: "Invoice recipient",
  notes: "Notes",
};

export function DealHistorySection({
  dealId,
  currency,
}: {
  dealId: string;
  currency: Currency;
}): ReactNode {
  const history = useDealHistory(dealId);
  return (
    <Card>
      <CardHeader title="History" />
      <CardBody>
        <QueryBoundary query={history} label="the deal's history">
          {(data) => <HistoryTable entries={data.entries} currency={currency} />}
        </QueryBoundary>
      </CardBody>
    </Card>
  );
}

function HistoryTable({
  entries,
  currency,
}: {
  entries: readonly DealHistoryEntry[];
  currency: Currency;
}): ReactNode {
  const [showAll, setShowAll] = useState(false);
  const newestFirst = [...entries].reverse();
  const shown = showAll ? newestFirst : newestFirst.slice(0, FIRST_SHOWN);
  return (
    <>
      <Table label="Deal history">
        <thead>
          <tr>
            <Th>When</Th>
            <Th>What</Th>
            <Th>Who</Th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && (
            <EmptyRow
              colSpan={3}
              title="No history"
              description="Nothing has been recorded for this deal yet."
            />
          )}
          {shown.map((entry, index) => (
            <tr key={`${entry.at}-${entry.kind}-${index}`}>
              <Td nowrap muted>
                {formatInstant(entry.at)}
                {entry.approximate && (
                  <div className={styles.note}>Dated by a HubSpot bulk edit</div>
                )}
              </Td>
              <Td>
                <EntryText entry={entry} currency={currency} />
              </Td>
              <Td nowrap muted>
                <Who entry={entry} />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {newestFirst.length > FIRST_SHOWN && (
        <div className={styles.more}>
          <Button variant="subtle" onClick={() => setShowAll(!showAll)}>
            {showAll ? "Show fewer" : `Show all ${newestFirst.length}`}
          </Button>
        </div>
      )}
    </>
  );
}

function EntryText({
  entry,
  currency,
}: {
  entry: DealHistoryEntry;
  currency: Currency;
}): ReactNode {
  if (entry.kind === "stage") {
    const to = entry.to_state === null ? "an unknown stage" : dealStateLabel(entry.to_state);
    return entry.from_state === null ? (
      <span>
        Opened as <strong>{to}</strong>
      </span>
    ) : (
      <span>
        {dealStateLabel(entry.from_state)} → <strong>{to}</strong>
      </span>
    );
  }
  return (
    <ul className={styles.changes}>
      {entry.changes.map((change) => (
        <li key={change.field}>
          <span className={styles.field}>{FIELD_LABEL[change.field] ?? change.field}</span>{" "}
          {fieldValue(change.field, change.before, currency)} →{" "}
          <strong>{fieldValue(change.field, change.after, currency)}</strong>
        </li>
      ))}
    </ul>
  );
}

/** An audit value as a reader says it: money as money, a probability as a percent, nothing as a dash. */
export function fieldValue(field: string, value: string | null, currency: Currency): string {
  if (value === null || value === "") return "—";
  if (field.endsWith("_minor")) {
    const minor = Number(value);
    return Number.isFinite(minor) ? formatMoney(minor, currency) : value;
  }
  if (field === "win_prob") return `${value}%`;
  return value;
}

function Who({ entry }: { entry: DealHistoryEntry }): ReactNode {
  const principal = usePrincipal();
  if (entry.stage_source === "imported") return <Badge tone="slate">HubSpot</Badge>;
  switch (entry.actor_type) {
    case null:
      return "";
    case "system":
      return "Reconstructed";
    case "agent":
      return <Badge tone="info">Agent</Badge>;
    case "user":
      return entry.actor_subject !== null && entry.actor_subject === principal.subject
        ? "You"
        : "A person";
  }
}
